import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Download, ArrowLeft } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getBatchRun, downloadBatchRunZip, type BatchRunResponse } from '@/api/batchReportsApi';

const POLL_INTERVAL_MS = 2000;

function capitaliseStatus(status: string): string {
  if (!status) return status;
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'complete':
      return 'default';
    case 'failed':
      return 'destructive';
    case 'running':
    case 'pending':
      return 'secondary';
    default:
      return 'outline';
  }
}

export interface BatchRunProgressProps {
  batchRunId: string;
  className?: string;
}

export const BatchRunProgress: React.FC<BatchRunProgressProps> = ({
  batchRunId,
  className = '',
}) => {
  const navigate = useNavigate();
  const { data: run, isLoading, isError, error } = useQuery<BatchRunResponse>({
    queryKey: ['batchRun', batchRunId],
    queryFn: () => getBatchRun(batchRunId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'complete' || status === 'partial' || status === 'failed') return false;
      return POLL_INTERVAL_MS;
    },
    enabled: !!batchRunId,
  });

  const handleDownload = useCallback(async () => {
    if (!run?.download_url) return;
    try {
      const blob = await downloadBatchRunZip(batchRunId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Reports_${batchRunId}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed:', e);
    }
  }, [batchRunId, run?.download_url]);

  const canDownload =
    run && (run.status === 'complete' || run.status === 'partial') && run.download_url;

  return (
    <div className={className}>
      <PageHeader
        title="Batch Run Progress"
        subtitle={run ? `Status: ${capitaliseStatus(run.status)}` : undefined}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/app/reports/batch')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            {canDownload && (
              <Button size="sm" onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download Zip
              </Button>
            )}
          </div>
        }
      />

      <div className="mt-4 space-y-4">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        )}
        {isError && (
          <div className="text-sm text-destructive">
            {(error as Error).message || 'Failed to load run'}
          </div>
        )}
        {run && (
          <ul className="space-y-2 border border-border rounded-lg divide-y divide-border bg-card">
            {run.items.map((item) => (
              <li key={item.id} className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <span className="text-sm font-medium text-foreground">
                    {item.template_name ?? item.template_id}
                  </span>
                  {item.error_message && (
                    <p className="text-xs text-destructive mt-1 truncate" title={item.error_message}>
                      {item.error_message}
                    </p>
                  )}
                </div>
                <Badge variant={statusVariant(item.status)}>
                  {item.status === 'running' ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : null}
                  {capitaliseStatus(item.status)}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
